import React, { createContext, useContext, useState, useEffect } from "react";
import { collection, query, where, addDoc, deleteDoc, doc, updateDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "./AuthContext";

const NotesContext = createContext(null);

export function useNotes() {
    const ctx = useContext(NotesContext);
    if (!ctx) throw new Error("useNotes must be used inside <NotesProvider>");
    return ctx;
}

export function NotesProvider({ children }) {
    const { userProfile } = useAuth();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ── Listen to user's notes ────────────────────────────────────
    useEffect(() => {
        if (!userProfile?.uid) {
            setNotes([]);
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, "notes"),
            where("userId", "==", userProfile.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            try {
                const notesData = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setNotes(notesData);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        });

        return unsubscribe;
    }, [userProfile?.uid]);

    // ── Add new note ────────────────────────────────────────────
    const addNote = async (title, content, category = "general", reminderDate = null) => {
        try {
            const newNote = {
                userId: userProfile.uid,
                displayName: userProfile.displayName,
                title,
                content,
                category, // "general", "reminder", "urgent", "todo"
                reminderDate, // optional YYYY-MM-DD
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                isPinned: false,
            };

            const docRef = await addDoc(collection(db, "notes"), newNote);
            return docRef.id;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // ── Update note ─────────────────────────────────────────────
    const updateNote = async (noteId, title, content, category, reminderDate = null) => {
        try {
            const noteRef = doc(db, "notes", noteId);
            await updateDoc(noteRef, {
                title,
                content,
                category,
                reminderDate,
                updatedAt: serverTimestamp(),
            });
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // ── Delete note ─────────────────────────────────────────────
    const deleteNote = async (noteId) => {
        try {
            await deleteDoc(doc(db, "notes", noteId));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // ── Pin/Unpin note ──────────────────────────────────────────
    const togglePinNote = async (noteId, isPinned) => {
        try {
            const noteRef = doc(db, "notes", noteId);
            await updateDoc(noteRef, {
                isPinned: !isPinned,
                updatedAt: serverTimestamp(),
            });
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    return (
        <NotesContext.Provider
            value={{
                notes,
                loading,
                error,
                addNote,
                updateNote,
                deleteNote,
                togglePinNote,
            }}
        >
            {children}
        </NotesContext.Provider>
    );
}
